// "Logo" — Practice Details card (practice logos, 2026-10-19).
//
// The practice's logo, drawn at the top of every report Atlas sends to the practice
// or on its behalf: statements, the collections invoice, remittance advice, the
// roster, and the weekly client and Waterfall emails. A practice with no logo gets
// the name-only header it always had.
//
// The image is fetched as a blob, never put in an <img src="/api/...">: the Clerk
// JWT rides only on window.fetch, so a plain image request would arrive unsigned.
// Read-only users see the logo and no buttons; the server refuses their writes too.

import React, { useEffect, useRef, useState } from "react";

import "./PracticeLogoCard.css";

const API_URL = process.env.REACT_APP_API_URL || "";
const MAX_KB = 512;

const logoUrl = (practiceId) => `${API_URL}/api/practices/${practiceId}/logo`;

async function errorFrom(response, fallback) {
  const body = await response.json().catch(() => null);
  return new Error((body && body.error) || fallback);
}

export default function PracticeLogoCard({ practiceId, readOnly }) {
  const [src, setSrc] = useState(null); // object URL of the stored logo
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [message, setMessage] = useState(null); // {type, text}
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const currentPractice = useRef(practiceId);
  currentPractice.current = practiceId;

  const load = async (forPractice) => {
    setLoadError(null);
    try {
      const response = await fetch(logoUrl(forPractice), { credentials: "include" });
      if (currentPractice.current !== forPractice) return;
      if (response.status === 404) {
        setSrc(null);
      } else if (!response.ok) {
        throw await errorFrom(response, "Could not load the logo.");
      } else {
        const blob = await response.blob();
        if (currentPractice.current !== forPractice) return;
        setSrc(URL.createObjectURL(blob));
      }
    } catch (err) {
      if (currentPractice.current === forPractice) setLoadError(err.message || "Could not load the logo.");
    } finally {
      if (currentPractice.current === forPractice) setLoaded(true);
    }
  };

  useEffect(() => {
    setSrc(null);
    setLoaded(false);
    setMessage(null);
    if (practiceId) load(practiceId);
  }, [practiceId]);

  // Release each object URL when it is replaced or the card goes away.
  useEffect(() => () => {
    if (src) URL.revokeObjectURL(src);
  }, [src]);

  if (!practiceId) return null;

  const upload = async (file) => {
    if (!file || busy) return;
    if (file.size > MAX_KB * 1024) {
      setMessage({ type: "error", text: `The logo must be ${MAX_KB} KB or smaller.` });
      return;
    }
    const forPractice = practiceId;
    setBusy(true);
    setMessage(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const response = await fetch(logoUrl(forPractice), {
        method: "PUT",
        credentials: "include",
        body: form,
      });
      if (!response.ok) throw await errorFrom(response, "Could not upload the logo.");
      if (currentPractice.current !== forPractice) return;
      await load(forPractice);
      setMessage({ type: "success", text: "Logo saved. Reports sent from now on carry it." });
    } catch (err) {
      if (currentPractice.current === forPractice) {
        setMessage({ type: "error", text: err.message || "Could not upload the logo." });
      }
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    if (busy) return;
    const forPractice = practiceId;
    setBusy(true);
    setMessage(null);
    try {
      const response = await fetch(logoUrl(forPractice), { method: "DELETE", credentials: "include" });
      if (!response.ok) throw await errorFrom(response, "Could not remove the logo.");
      if (currentPractice.current !== forPractice) return;
      setSrc(null);
      setMessage({ type: "success", text: "Logo removed. Reports go back to the practice name alone." });
    } catch (err) {
      if (currentPractice.current === forPractice) {
        setMessage({ type: "error", text: err.message || "Could not remove the logo." });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="integrations-section practice-logo-card">
      <h3>Logo</h3>
      <p className="practice-logo-muted practice-logo-intro">
        The logo will be included in all patient statements and invoices created by the
        system. PNG or JPEG, up to {MAX_KB} KB.
      </p>

      {message && (
        <div className={`practice-logo-message practice-logo-message-${message.type}`}>
          {message.text}
          <button
            type="button"
            className="practice-logo-message-close"
            onClick={() => setMessage(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {loadError ? (
        <div className="practice-logo-message practice-logo-message-error">{loadError}</div>
      ) : !loaded ? (
        <p className="practice-logo-muted">Loading…</p>
      ) : (
        <div className="practice-logo-body">
          <div className="practice-logo-preview">
            {src ? (
              <img src={src} alt="Practice logo" />
            ) : (
              <span className="practice-logo-muted">No logo</span>
            )}
          </div>
          {!readOnly && (
            <div className="practice-logo-actions">
              <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="practice-logo-input"
                aria-label="Logo file"
                onChange={(e) => upload(e.target.files && e.target.files[0])}
                disabled={busy}
              />
              <button
                type="button"
                className="practice-logo-btn practice-logo-btn-primary"
                onClick={() => inputRef.current && inputRef.current.click()}
                disabled={busy}
              >
                {busy ? "Saving…" : src ? "Replace" : "Upload"}
              </button>
              {src && (
                <button type="button" className="practice-logo-btn" onClick={remove} disabled={busy}>
                  Remove
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
